import { Box, Text } from "@chakra-ui/react";
import { usePlannedStartLabel } from "../needs/plannedStart";
import { t } from "i18next";
import { ReactElement } from "react";
import { Trans } from "react-i18next";
import {
  OrganizationMergeRequestStatus,
  type OrganizationMergeNotificationMetaDto,
  ContractDateReminderType,
  ContractDocumentNotificationMetaDto,
  ContractNotificationMetaDto,
  ContractReminderNotificationMetaDto,
  ContractTaskNotificationMetaDto,
  NotificationDto,
  NotificationTypeDto,
  SeBidDeliveryKind,
  SeDoffinPublicationFailedNotificationMetaDto,
  SeDoffinPublishedNotificationMetaDto,
  SeInvitedToTenderNotificationMetaDto,
  SeInvitedToConfirmInterestNotificationMetaDto,
  SeOrganizationInviteNotificationMetaDto,
  SeDpsAdmittedNotificationMetaDto,
  SeQualificationSchemeAdmittedNotificationMetaDto,
  SeQualificationSchemeRemovedNotificationMetaDto,
  SeQualificationSchemeRemovalWithdrawnNotificationMetaDto,
  SeDpsRemovedNotificationMetaDto,
  SeBidRejectedNotificationMetaDto,
  SeRequestNotQualifiedNotificationMetaDto,
  SeRequestNotSelectedNotificationMetaDto,
  SeResponsibleBuyerChangedNotificationMetaDto,
  NeedReportedNotificationMetaDto,
  NeedStatus,
  NeedStatusChangedNotificationMetaDto,
  SeJuryConfirmationRequestedNotificationMetaDto,
  FrameworkPanelJoinedNotificationMetaDto,
  FrameworkPanelStatusChangedNotificationMetaDto,
  FrameworkValueReminderNotificationMetaDto,
  SeMiniCompetitionInviteNotificationMetaDto,
  SourcingEventAnnouncementNotificationMetaDto,
  SourcingEventNotificationMetaDto,
  SourcingEventQuestionNotificationMetaDto,
} from "../../autogen/bff-api";
import { displayDate, type SupportedLocale } from "../formatting/displayDate";
import { displayDateWithTime } from "../formatting/displayDateWithTime";
import { displaySum } from "../formatting/displaySum";
import { displayWrappedWithEllipsis } from "../formatting/displayWrappedWithEllipsis";

const TaskMetaContext = ({ meta }: { meta: ContractTaskNotificationMetaDto }): ReactElement | null => {
  if (!meta.projectName && !meta.counterpartyName) {
    return null;
  }
  return (
    <Text fontSize={"xs"} color={"smMuted"} mt={"0.5"}>
      {meta.projectName ? (
        <>
          {t("Project")}: <b>{displayWrappedWithEllipsis(meta.projectName, 60)}</b>
        </>
      ) : null}
      {meta.projectName && meta.counterpartyName ? " · " : ""}
      {meta.counterpartyName ? (
        <>
          {t("Counterparty")}: <b>{displayWrappedWithEllipsis(meta.counterpartyName, 60)}</b>
        </>
      ) : null}
    </Text>
  );
};

export const getNotificationDescription = (
  notification: NotificationDto,
  locale?: SupportedLocale | null,
): ReactElement => {
  switch (notification.type) {
    case NotificationTypeDto.ContractTaskDelegated: {
      const meta = requireTaskMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("delegated the task")}{" "}
            <b>{displayWrappedWithEllipsis(meta.taskName, 100)} </b>
            {t("to you for the contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)}.</b>
          </Text>
          <TaskMetaContext meta={meta} />
        </Box>
      );
    }
    case NotificationTypeDto.ContractTaskStatusUpdated: {
      const meta = requireTaskMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("changed the status for the task")}{" "}
            <b>{displayWrappedWithEllipsis(meta.taskName, 100)} </b>
            {t("in the contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)}.</b>
          </Text>
          <TaskMetaContext meta={meta} />
        </Box>
      );
    }
    case NotificationTypeDto.ContractDateReminder: {
      const meta = requireContractReminderMeta(notification);

      const getNotificationName = (): string => {
        switch (meta.reminderType) {
          case ContractDateReminderType.EndDateReminder:
            return t("The end date");
          case ContractDateReminderType.RenewalDateReminder:
            return t("renewal date");
          case ContractDateReminderType.WarrantyExpirationDateReminder:
            return t("warranty expiration date");
          case ContractDateReminderType.StartDateReminder:
            return t("start date");
          case ContractDateReminderType.TerminationDateReminder:
            return t("termination date");
          case ContractDateReminderType.NoticePeriodReminder:
            return t("start of notice period");
          case ContractDateReminderType.OptionExecutionDeadlineReminder:
            return t("option execution deadline");
          case ContractDateReminderType.OptionStartDateReminder:
            return t("option start date");
          case ContractDateReminderType.NoEndDateReminder:
            return t("no end date");
        }
      };

      if (meta.reminderType === "NoEndDateReminder") {
        return (
          <Box>
            <Text fontSize={"sm"}>
              {t("You have an active contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)}</b>{" "}
              {t("without an end date created the")} <b>{displayDate(meta.date, locale)}</b>.
            </Text>
          </Box>
        );
      }

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The")} <b>{getNotificationName()}</b> {t("for the contract")}
            {" '"}
            {displayWrappedWithEllipsis(meta.contractName, 100)}
            {"' "}
            {t("is approaching at the")} <b>{displayDate(meta.date, locale)}</b>.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.ContractDocumentCompletedSigning: {
      const meta = requireContractDocumentMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The document")} <b>{displayWrappedWithEllipsis(meta.documentName, 100)} </b>
            {t("for the contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)} </b>
            {t("has completed signing!")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.ContractSignatureDeadlineExpired: {
      const meta = requireContractDocumentMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The deadline for signing the document")} <b>{displayWrappedWithEllipsis(meta.documentName, 100)} </b>
            {t("belonging to the contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)} </b>
            {t("has expired")}.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.ContractSharedWithDepartment: {
      const meta = requireContractkMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("shared the contract")}{" "}
            <b>{displayWrappedWithEllipsis(meta.contractName, 100)} </b>
            {t("with your department")}.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.ContractSharedWithOrg: {
      const meta = requireContractkMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("shared the contract")}{" "}
            <b>{displayWrappedWithEllipsis(meta.contractName, 100)} </b>
            {t("with your organization")}.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.CallOffCompletedByOutsideOrg: {
      const meta = requireContractkMeta(notification);
      const value =
        meta.value != null && meta.currency ? displaySum({ value: Number(meta.value), currency: meta.currency }) : null;

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("{{person}} at {{organization}} completed the call-off", {
              person: meta.triggeredByFirstName ?? "",
              organization: meta.organizationName ?? "",
            })}{" "}
            <b>{displayWrappedWithEllipsis(meta.contractName, 100)}</b>
            {meta.counterpartyName ? ` ${t("with {{supplier}}", { supplier: meta.counterpartyName })}` : ""}
            {value ? ` ${t("for {{value}}", { value })}` : ""}.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.ContractSharedWithPerson: {
      const meta = requireContractkMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("shared the contract")}{" "}
            <b>{displayWrappedWithEllipsis(meta.contractName, 100)} </b>
            {t("with you")}.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.ContractMessageReceived: {
      const meta = requireContractkMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("sent a message in the contract")}{" "}
            <b>{displayWrappedWithEllipsis(meta.contractName, 100)}.</b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.ContractTaskCommentAdded: {
      const meta = requireTaskMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("added a comment for the task")}{" "}
            <b>{displayWrappedWithEllipsis(meta.taskName, 100)} </b>
            {t("in the contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)}.</b>
          </Text>
          <TaskMetaContext meta={meta} />
        </Box>
      );
    }
    case NotificationTypeDto.ContractTaskDeleted: {
      const meta = requireTaskMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("deleted the task")} <b>{displayWrappedWithEllipsis(meta.taskName, 100)} </b>
            {t("in the contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)}.</b>
          </Text>
          <TaskMetaContext meta={meta} />
        </Box>
      );
    }
    case NotificationTypeDto.ContractTaskDueDateUpdated: {
      const meta = requireTaskMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("changed the due date for the task")}{" "}
            <b>{displayWrappedWithEllipsis(meta.taskName, 100)} </b>
            {t("in the contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)}.</b>
          </Text>
          <TaskMetaContext meta={meta} />
        </Box>
      );
    }
    case NotificationTypeDto.ContractTaskDueDateReminder: {
      const meta = requireTaskMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The due date is approaching for the task")} <b>{displayWrappedWithEllipsis(meta.taskName, 100)} </b>
            {t("in the contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)}.</b>
          </Text>
          <TaskMetaContext meta={meta} />
        </Box>
      );
    }
    case NotificationTypeDto.SeV2NewQuestion: {
      const meta = requireSourcingEventQuestionMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("A new question has been asked for the sourcing event")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2QuestionAnswered: {
      const meta = requireSourcingEventQuestionMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("Your question has been answered")}:{" "}
            <b>{displayWrappedWithEllipsis(meta.questionSubject, 100)}</b>.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2ClarificationMessageToSupplier: {
      const meta = requireSourcingEventQuestionMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The buyer has sent you a message in a clarification dialog")}:{" "}
            <b>{displayWrappedWithEllipsis(meta.questionSubject, 100)}</b>.
          </Text>
          {meta.replyDeadline && (
            <Text fontSize={"sm"}>
              {t("Reply deadline")}: {displayDateWithTime(meta.replyDeadline, t("at"), locale)}
            </Text>
          )}
        </Box>
      );
    }
    case NotificationTypeDto.SeV2ClarificationReplyDeadlineReminder: {
      const meta = requireSourcingEventQuestionMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The reply deadline for a clarification is approaching, and you have not replied yet")}:{" "}
            <b>{displayWrappedWithEllipsis(meta.questionSubject, 100)}</b>.
          </Text>
          {meta.replyDeadline && (
            <Text fontSize={"sm"}>
              {t("Reply deadline")}: {displayDateWithTime(meta.replyDeadline, t("at"), locale)}
            </Text>
          )}
        </Box>
      );
    }
    case NotificationTypeDto.SeV2ClarificationMessageToBuyer: {
      const meta = requireSourcingEventQuestionMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("A tenderer has replied in a clarification dialog")}:{" "}
            <b>{displayWrappedWithEllipsis(meta.questionSubject, 100)}</b>.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2InformationPublished: {
      const meta = requireSourcingEventAnnouncementMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("New information has been published for the sourcing event")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2AwardDecided: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The sourcing event")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("has been awarded!")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2AwardLetterDispatched: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("Your award letter for")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("has been sent.")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2InvitedToTender: {
      const meta = requireSeInvitedToTenderMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            <b>{meta.eventOwningOrganizationName}</b>{" "}
            {t("has invited you to tender in")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2InvitedToConfirmInterest: {
      const meta = requireSeInvitedToConfirmInterestMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            <b>{meta.eventOwningOrganizationName}</b>{" "}
            {t("invites you to confirm your interest in")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2OrganizationInvite: {
      const meta = requireSeOrganizationInviteMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            <b>{meta.eventOwningOrganizationName}</b>{" "}
            {t("has invited you to tender in")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2BidRejected: {
      const meta = requireSeBidRejectedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("Your bid in")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("was rejected")}
          </Text>
          {meta.rejectionReason && (
            <Text fontSize={"xs"} color={"smMuted"} mt={"0.5"}>
              {displayWrappedWithEllipsis(meta.rejectionReason, 120)}
            </Text>
          )}
        </Box>
      );
    }
    case NotificationTypeDto.SeV2BidRejectionRevoked: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("Your bid in")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("is back in the competition: the rejection has been withdrawn")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2RequestNotQualified: {
      const meta = requireSeRequestNotQualifiedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("Your request to participate in")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("was not qualified")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2DpsAdmitted: {
      const meta = requireSeDpsAdmittedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("You are qualified in")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>.{" "}
            {t("You will be invited to every competition under the system.")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2QualificationSchemeAdmitted: {
      const meta = requireSeQualificationSchemeAdmittedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("You are qualified in")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>.{" "}
            {t("You are on the supplier list and may be invited to competitions under the system.")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2QualificationSchemeRemoved: {
      const meta = requireSeQualificationSchemeRemovedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.removalEffectiveAt ? (
              <>
                {t("From {{date}} you are no longer on the supplier list in", {
                  date: displayDate(meta.removalEffectiveAt, locale),
                })}{" "}
                <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>.{" "}
              </>
            ) : (
              <>
                {t("You have been removed from")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>.{" "}
              </>
            )}
            {meta.removalReason}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2QualificationSchemeRemovalWithdrawn: {
      const meta = requireSeQualificationSchemeRemovalWithdrawnMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("Your removal from")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("has been withdrawn. You are still on the supplier list.")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2DpsRemoved: {
      const meta = requireSeDpsRemovedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("You have been removed from")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>.{" "}
            {meta.removalReason}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2RequestNotSelected: {
      const meta = requireSeRequestNotSelectedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("Not selected for tender")}: <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>.{" "}
            {t("Your organization was qualified, but not among the suppliers invited to tender.")}
            {meta.selectionReason ? ` ${meta.selectionReason}` : ""}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2AwardDecisionRevoked: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The award decision for")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("has been withdrawn")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2BidDeadlineReminder: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The bid delivery deadline for")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)} </b>
            {t("is approaching")}{" "}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2ResultNoticeDeadlineReminder: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The contract award for")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("must be announced on Doffin by {{date}}", { date: meta.deadline ? displayDate(meta.deadline) : "" })}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2ResponsibleBuyerChanged: {
      const meta = requireSeResponsibleBuyerChangedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The responsible buyer for")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("changed from")} <b>{meta.previousResponsibleName}</b> {t("to")} <b>{meta.newResponsibleName}</b>.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2JuryConfirmationRequested: {
      const meta = requireSeJuryConfirmationRequestedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The jury's report in")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("awaits your confirmation.")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2EventCancelled: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The sourcing event")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("has been cancelled")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2EventClosed: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The sourcing event")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("has been closed")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2BidDelivered: {
      const meta = requireSourcingEventMeta(notification);
      const deliveryText = {
        [SeBidDeliveryKind.Revised]: t("has delivered a new version of their bid for"),
        [SeBidDeliveryKind.Round]: t("has delivered a bid for the negotiation round in"),
        [SeBidDeliveryKind.First]: t("has delivered a bid for"),
      }[meta.bidDeliveryKind ?? SeBidDeliveryKind.First];

      return (
        <Box>
          <Text fontSize={"sm"}>
            <b>{meta.supplierOrganizationName}</b> {deliveryText}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2MiniCompetitionInvite: {
      const meta = requireSeV2MiniCompetitionInviteMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            <b>{meta.eventOwningOrganizationName}</b> {t("has invited you to the mini-competition")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.FrameworkPanelJoined: {
      const meta = requireFrameworkPanelJoinedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            <b>{meta.buyerOrganizationName}</b> {t("has concluded the framework agreement")}{" "}
            <b>{displayWrappedWithEllipsis(meta.frameworkAgreementTitle, 100)}</b>{" "}
            {t("with your company as one of its suppliers")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.FrameworkValueReminder: {
      const meta = requireFrameworkValueReminderMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            <Trans
              t={t}
              i18nKey="The call-offs under <b>{{title}}</b> have passed <b>{{percentage}} %</b> of its ceiling value, and are now at {{percentUsed}} %."
              values={{
                title: displayWrappedWithEllipsis(meta.frameworkAgreementTitle, 100),
                percentage: meta.percentage,
                percentUsed: meta.percentUsed,
              }}
              components={{ b: <b /> }}
            />
          </Text>
          <Text fontSize={"sm"}>
            {t("Used")}: <b>{displaySum({ value: Number(meta.usedValue), currency: meta.currency })}</b> ·{" "}
            {t("Left")}: <b>{displaySum({ value: Number(meta.remainingValue), currency: meta.currency })}</b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.FrameworkPanelStatusChanged: {
      const meta = requireFrameworkPanelStatusChangedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            <b>{meta.buyerOrganizationName}</b> {t("has changed your status on the framework agreement")}{" "}
            <b>{displayWrappedWithEllipsis(meta.frameworkAgreementTitle, 100)}</b> {t("to")} {t(meta.status)}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2RoundPublished: {
      const meta = requireSourcingEventMeta(notification);

      if (meta.isFinalRound && meta.roundDeadline) {
        return (
          <Box>
            <Text fontSize={"sm"}>
              <b>{meta.eventOwningOrganizationName}</b> {t("has invited you to the final negotiation round in")}{" "}
              <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>.{" "}
              {t("The deadline is {{deadline}}, and it is your last chance to revise the bid.", {
                deadline: displayDateWithTime(meta.roundDeadline, t("at"), locale),
              })}
            </Text>
          </Box>
        );
      }

      return (
        <Box>
          <Text fontSize={"sm"}>
            <b>{meta.eventOwningOrganizationName}</b> {t("has invited you to a negotiation round in")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2EventChanged: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The sourcing event")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("has been changed")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2DoffinPublished: {
      const meta = requireSeDoffinPublishedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.isChangeNotice ? t("The change notice for") : t("The contract notice for")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b> {t("has been published on Doffin")}.
          </Text>
          <Text fontSize={"xs"} color={"smMuted"} mt={"0.5"}>
            {t("Doffin reference")}: <b>{meta.doffinReference}</b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2DoffinPublicationFailed: {
      const meta = requireSeDoffinPublicationFailedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.isChangeNotice ? t("The change notice for") : t("The contract notice for")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b> {t("was not published on Doffin")}.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.OrganizationMerge: {
      const meta = requireOrganizationMergeMeta(notification);
      const company = meta.counterpartOrganizationName;
      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.status === OrganizationMergeRequestStatus.Pending &&
              t("{{company}} asks to merge with your company", { company })}
            {meta.status === OrganizationMergeRequestStatus.Merged &&
              t("{{company}} has been merged into your company", { company })}
            {meta.status === OrganizationMergeRequestStatus.Declined &&
              t("{{company}} declined the request to merge", { company })}
            {meta.status === OrganizationMergeRequestStatus.Expired &&
              t("The request to merge with {{company}} has expired", { company })}
            {meta.status === OrganizationMergeRequestStatus.Cancelled &&
              t("The request to merge with {{company}} was cancelled", { company })}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.NeedReported: {
      const meta = requireNeedReportedMeta(notification);
      return (
        <Box>
          <Text fontSize={"sm"}>
            <Trans
              i18nKey={'New need: <bold>"{{title}}"</bold> from {{name}}'}
              values={{ title: displayWrappedWithEllipsis(meta.needTitle, 100), name: meta.reporterName }}
              components={{ bold: <b /> }}
            />
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.NeedStatusChanged: {
      const meta = requireNeedStatusChangedMeta(notification);
      return (
        <Box>
          <Text fontSize={"sm"}>
            <NeedStatusChangedText meta={meta} />
          </Text>
        </Box>
      );
    }
  }
};

const NeedStatusChangedText = ({ meta }: { meta: NeedStatusChangedNotificationMetaDto }) => {
  const plannedStartLabel = usePlannedStartLabel();
  const values = {
    title: displayWrappedWithEllipsis(meta.needTitle, 100),
    comment: meta.comment ?? "",
    month: meta.plannedStart ? plannedStartLabel(meta.plannedStart) : "",
  };
  const components = { bold: <b /> };
  switch (meta.status) {
    case NeedStatus.SentBack:
      return (
        <Trans
          i18nKey={'The need <bold>"{{title}}"</bold> has been sent back to you: {{comment}}'}
          values={values}
          components={components}
        />
      );
    case NeedStatus.Rejected:
      return (
        <Trans i18nKey={'The need <bold>"{{title}}"</bold> has been rejected'} values={values} components={components} />
      );
    case NeedStatus.Planned:
      return meta.plannedStart ? (
        <Trans i18nKey={'The need <bold>"{{title}}"</bold> is planned to start in {{month}}'} values={values} components={components} />
      ) : (
        <Trans i18nKey={'The need <bold>"{{title}}"</bold> is planned'} values={values} components={components} />
      );
    case NeedStatus.ProcurementCreated:
      return (
        <Trans
          i18nKey={'A procurement has been created from the need <bold>"{{title}}"</bold>'}
          values={values}
          components={components}
        />
      );
    case NeedStatus.UnderCallOff:
      return (
        <Trans
          i18nKey={'The need <bold>"{{title}}"</bold> is now met through a framework agreement'}
          values={values}
          components={components}
        />
      );
    default:
      return (
        <Trans i18nKey={'The need <bold>"{{title}}"</bold> is with procurement'} values={values} components={components} />
      );
  }
};

export const requireNeedStatusChangedMeta = (notification: NotificationDto): NeedStatusChangedNotificationMetaDto => {
  if (!notification.meta.needStatusChanged) {
    throw new Error("should never happen");
  }
  return notification.meta.needStatusChanged;
};

export const requireNeedReportedMeta = (notification: NotificationDto): NeedReportedNotificationMetaDto => {
  if (!notification.meta.needReported) {
    throw new Error("should never happen");
  }
  return notification.meta.needReported;
};

export const requireOrganizationMergeMeta = (notification: NotificationDto): OrganizationMergeNotificationMetaDto => {
  if (!notification.meta.organizationMerge) {
    throw new Error("should never happen");
  }
  return notification.meta.organizationMerge;
};

export const requireTaskMeta = (notification: NotificationDto): ContractTaskNotificationMetaDto => {
  if (!notification.meta.contractTask) {
    throw new Error("should never happen");
  }
  return notification.meta.contractTask;
};

export const requireContractkMeta = (notification: NotificationDto): ContractNotificationMetaDto => {
  if (!notification.meta.contract) {
    throw new Error("should never happen");
  }
  return notification.meta.contract;
};

export const requireContractReminderMeta = (notification: NotificationDto): ContractReminderNotificationMetaDto => {
  if (!notification.meta.contractReminder) {
    throw new Error("should never happen");
  }
  return notification.meta.contractReminder;
};

export const requireContractDocumentMeta = (notification: NotificationDto): ContractDocumentNotificationMetaDto => {
  if (!notification.meta.contractDocument) {
    throw new Error("should never happen");
  }
  return notification.meta.contractDocument;
};

export const requireSeV2MiniCompetitionInviteMeta = (
  notification: NotificationDto
): SeMiniCompetitionInviteNotificationMetaDto => {
  if (!notification.meta.seV2MiniCompetitionInvite) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2MiniCompetitionInvite;
};

export const requireFrameworkPanelJoinedMeta = (
  notification: NotificationDto
): FrameworkPanelJoinedNotificationMetaDto => {
  if (!notification.meta.frameworkPanelJoined) {
    throw new Error("should never happen");
  }
  return notification.meta.frameworkPanelJoined;
};

export const requireFrameworkValueReminderMeta = (
  notification: NotificationDto
): FrameworkValueReminderNotificationMetaDto => {
  if (!notification.meta.frameworkValueReminder) {
    throw new Error("should never happen");
  }
  return notification.meta.frameworkValueReminder;
};

export const requireFrameworkPanelStatusChangedMeta = (
  notification: NotificationDto
): FrameworkPanelStatusChangedNotificationMetaDto => {
  if (!notification.meta.frameworkPanelStatusChanged) {
    throw new Error("should never happen");
  }
  return notification.meta.frameworkPanelStatusChanged;
};

export const requireSourcingEventMeta = (notification: NotificationDto): SourcingEventNotificationMetaDto => {
  if (!notification.meta.sourcingEvent) {
    throw new Error("should never happen");
  }
  return notification.meta.sourcingEvent;
};

export const requireSeInvitedToTenderMeta = (
  notification: NotificationDto
): SeInvitedToTenderNotificationMetaDto => {
  if (!notification.meta.seV2InvitedToTender) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2InvitedToTender;
};

export const requireSeInvitedToConfirmInterestMeta = (
  notification: NotificationDto
): SeInvitedToConfirmInterestNotificationMetaDto => {
  if (!notification.meta.seV2InvitedToConfirmInterest) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2InvitedToConfirmInterest;
};

export const requireSeOrganizationInviteMeta = (
  notification: NotificationDto
): SeOrganizationInviteNotificationMetaDto => {
  if (!notification.meta.seV2OrganizationInvite) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2OrganizationInvite;
};

export const requireSeBidRejectedMeta = (
  notification: NotificationDto
): SeBidRejectedNotificationMetaDto => {
  if (!notification.meta.seV2BidRejected) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2BidRejected;
};

export const requireSeRequestNotQualifiedMeta = (
  notification: NotificationDto
): SeRequestNotQualifiedNotificationMetaDto => {
  if (!notification.meta.seV2RequestNotQualified) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2RequestNotQualified;
};

export const requireSeDpsAdmittedMeta = (
  notification: NotificationDto
): SeDpsAdmittedNotificationMetaDto => {
  if (!notification.meta.seV2DpsAdmitted) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2DpsAdmitted;
};

export const requireSeQualificationSchemeAdmittedMeta = (
  notification: NotificationDto
): SeQualificationSchemeAdmittedNotificationMetaDto => {
  if (!notification.meta.seV2QualificationSchemeAdmitted) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2QualificationSchemeAdmitted;
};

export const requireSeQualificationSchemeRemovedMeta = (
  notification: NotificationDto
): SeQualificationSchemeRemovedNotificationMetaDto => {
  if (!notification.meta.seV2QualificationSchemeRemoved) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2QualificationSchemeRemoved;
};

export const requireSeQualificationSchemeRemovalWithdrawnMeta = (
  notification: NotificationDto,
): SeQualificationSchemeRemovalWithdrawnNotificationMetaDto => {
  if (!notification.meta.seV2QualificationSchemeRemovalWithdrawn) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2QualificationSchemeRemovalWithdrawn;
};

export const requireSeDpsRemovedMeta = (
  notification: NotificationDto
): SeDpsRemovedNotificationMetaDto => {
  if (!notification.meta.seV2DpsRemoved) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2DpsRemoved;
};

export const requireSeRequestNotSelectedMeta = (
  notification: NotificationDto
): SeRequestNotSelectedNotificationMetaDto => {
  if (!notification.meta.seV2RequestNotSelected) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2RequestNotSelected;
};

export const requireSeJuryConfirmationRequestedMeta = (
  notification: NotificationDto
): SeJuryConfirmationRequestedNotificationMetaDto => {
  if (!notification.meta.seV2JuryConfirmationRequested) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2JuryConfirmationRequested;
};

export const requireSeResponsibleBuyerChangedMeta = (
  notification: NotificationDto
): SeResponsibleBuyerChangedNotificationMetaDto => {
  if (!notification.meta.seV2ResponsibleBuyerChanged) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2ResponsibleBuyerChanged;
};

export const requireSeDoffinPublishedMeta = (
  notification: NotificationDto
): SeDoffinPublishedNotificationMetaDto => {
  if (!notification.meta.seV2DoffinPublished) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2DoffinPublished;
};

export const requireSeDoffinPublicationFailedMeta = (
  notification: NotificationDto
): SeDoffinPublicationFailedNotificationMetaDto => {
  if (!notification.meta.seV2DoffinPublicationFailed) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2DoffinPublicationFailed;
};


export const requireSourcingEventQuestionMeta = (
  notification: NotificationDto
): SourcingEventQuestionNotificationMetaDto => {
  if (!notification.meta.sourcingEventQuestion) {
    throw new Error("should never happen");
  }
  return notification.meta.sourcingEventQuestion;
};


export const requireSourcingEventAnnouncementMeta = (
  notification: NotificationDto
): SourcingEventAnnouncementNotificationMetaDto => {
  if (!notification.meta.sourcingEventAnnouncement) {
    throw new Error("should never happen");
  }
  return notification.meta.sourcingEventAnnouncement;
};
